/**
 * @module app.ext.mcg-informed
 */

import { autoinject, bindable } from 'aurelia-framework';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { McgInformedService } from 'app/ext/mcg-informed/services/mcg-informed-service';
import { EssDialogCustomElement } from 'app/shared/elements/ess-dialog';
import { I18N } from 'aurelia-i18n';
import { McgInformedDataMethods as dataMethods, SanitizationResults } from 'app/ext/mcg-informed/data-methods';
import { Toaster } from 'app/shared/lib/toaster';
import { Key } from 'app/shared/lib/keys';
import * as Mcg from 'app/ext/mcg-informed/interfaces';

@autoinject
export class AnnotatableDisplay extends ComponentBase {

    ////****************** Fields and Properties ************************************////

    @bindable
    protected annotatable: Mcg.IAnnotatable | null = null;

    @bindable
    protected sequence: number | null = null;

    @bindable
    protected version: string | null = null;

    @bindable
    public sanitizedText: string | null = null;

    @bindable
    protected rawText: string | null = null;

    @bindable
    public qualityMeasuresModal: EssDialogCustomElement | null = null;

    @bindable
    protected previewLength: number | null = null;

    @bindable
    protected labelPrefix: string | null = null;

    @bindable
    protected labelId: string | null = null;

    protected footnoteNumbers: string = '';

    protected annotatables: Mcg.IAnnotatable[] = [];
    protected annotatableStrings: string[] = [];

    protected hasPdfLinks: boolean = false;
    protected hasQualityMeasures: boolean = false;

    protected englishPdfHsim: string | null = null;
    protected spanishPdfHsim: string | null = null;

    protected showFootnotesI18n: string = this.i18n.tr('ui:actions.show', { what: '$t(ui:labels.footnote.$_plural)' });
    protected hideFootnotesI18n: string = this.i18n.tr('ui:actions.hide', { what: '$t(ui:labels.footnote.$_plural)' });

    ////****************** Lifecycle events (in order of execution) *****************////

    constructor(
        private readonly mcgService: McgInformedService,
        private readonly i18n: I18N,
        private readonly toaster: Toaster,
        ...rest: any[]
    ) {

        super(...rest);
        this.logger.source = this;
    }

    protected async bind(): Promise<void> {
        if(this.annotatable) {
            await this.sanitizeText();
            return;
        }

        if(!this.rawText || !this.version) return;
        this.hasPdfLinks = this.detectPdfLink(this.rawText);

        if(this.hasPdfLinks) this.sanitizePdfLinks(this.rawText);
        this.version = dataMethods.sanitizeVersion(this.version);
    }

    protected handleKeyDown(event: Event) {
        const key = (event as KeyboardEvent).key;
        if(key == Key.Space || key == Key.Enter) {
            return false;
        }
        return true;
    }

    protected toggleFootnotes(footnotesLink: HTMLAnchorElement): void {
        let footnoteDetails: HTMLElement = footnotesLink;

        footnotesLink.parentElement?.childNodes.forEach(n => {
            if(n.nodeName == 'DIV') {
                const e: HTMLElement = <HTMLElement>n;
                if(e.classList.contains('footnote-details')) footnoteDetails = e;
            }
        });

        if(footnoteDetails == footnotesLink) return;

        if(footnotesLink.innerHTML === this.showFootnotesI18n) footnotesLink.innerHTML = this.hideFootnotesI18n;
        else footnotesLink.innerHTML = this.showFootnotesI18n;
        footnoteDetails.classList.toggle('footnote-details-hide');
    }

    protected async openEnglishPdf(): Promise<void> {
        if(!this.englishPdfHsim || !this.version) return;
        await this.openPdf(this.englishPdfHsim);
    }

    protected async openSpanishPdf(): Promise<void> {
        if(!this.spanishPdfHsim || !this.version) return;
        await this.openPdf(this.spanishPdfHsim);
    }

    protected openQualityMeasures() {
        if(this.qualityMeasuresModal == null) return;
        this.qualityMeasuresModal.open();
    }

    ////****************** Functions ************************************************////

    protected async openPdf(hsim: string): Promise<void> {
        if(!hsim || !this.version) return;

        try {
            const response = await this.mcgService.loadPdf(hsim, this.version);

            var url = URL.createObjectURL(response);
            window.open(url, 'McgPdf');
        }
        catch(error) {
            this.toaster.error().message(this.i18n.tr('ui:messages.errors.open', { what: this.i18n.tr('ui:labels.pdf') })).show();
            throw error;
        }
    }

    private async sanitizeText(): Promise<void> {
        if(!this.annotatable) return;

        let s: string = this.isQuestion() ? (<Mcg.Question>this.annotatable).questionText : this.annotatable.text;
        let hasAnnotations: boolean = (this.annotatable.annotationSet && this.annotatable.annotationSet.length > 0) || (this.annotatable.citationSet && this.annotatable.citationSet.length > 0) || (this.annotatable.footnoteSet && this.annotatable.footnoteSet.length > 0);
        this.hasPdfLinks = this.detectPdfLink(s);

        if(hasAnnotations) s = this.sanitizeAnnotations(s);
        if(this.hasPdfLinks) s = this.sanitizePdfLinks(s);

        s = s.trim();
        s = s.replace(/\<emphasis\>/g, '\<em\>').replace(/\<\/emphasis\>/g, '\<\/em\>');

        this.hasQualityMeasures = s.indexOf('[[QM]]') > -1;
        if(this.hasQualityMeasures) s = s.replace('[[QM]]', '');

        this.sanitizedText = s;

        this.annotatables.forEach(a => {
            this.footnoteNumbers = this.footnoteNumbers + '[' + a.sequence + ']';
        });

        this.footnoteNumbers = this.footnoteNumbers.replace(/\]\[/g, '] [');
    }

    private sanitizeAnnotations(annotatableText: string): string {
        if(!this.annotatable) return annotatableText;

        const results: SanitizationResults = dataMethods.sanitizeAnnotations(this.annotatable, annotatableText);
        this.annotatableStrings.push(...results.annotatableStrings);
        this.annotatables.push(...results.annotatables);
        annotatableText = results.annotatableText;

        return annotatableText;
    }

    private sanitizePdfLinks(annotatableText: string): string {
        this.englishPdfHsim = annotatableText.substring(annotatableText.indexOf("hsim=\"") + 6, annotatableText.indexOf("\"", annotatableText.indexOf("hsim=\"") + 6));
        this.spanishPdfHsim = annotatableText.substring(annotatableText.indexOf("sp_hsim=\"") + 9, annotatableText.indexOf("\"", annotatableText.indexOf("sp_hsim=\"") + 9));

        return annotatableText.substring(annotatableText.indexOf(">") + 1, annotatableText.indexOf("</ulink>"));
    }

    private isQuestion(): boolean {
        return (<Mcg.Question>this.annotatable).questionText?.length > 0;
    }

    private detectPdfLink(rawText: string): boolean {
        return (rawText.indexOf('<ulink') > -1 && !!rawText.match(/url="\w*\.pdf"/gi));
    }

}
